import type { Dictionary } from "../../types";

/**
 * German is the structural source of truth — every other locale is typed against it,
 * so a forgotten key is a compile error rather than a missing string at runtime.
 *
 * Purposes are deliberately concrete. The DSK explicitly rejects wording like
 * "das Surferlebnis verbessern" as too vague to support informed consent.
 */
export const de: Dictionary = {
  banner: {
    title: "Datenschutz-Einstellungen",
    body:
      "Wir verwenden Cookies und ähnliche Technologien. Technisch notwendige setzen wir immer ein. " +
      "Alle anderen nur, wenn du zustimmst. Du kannst deine Entscheidung jederzeit ändern.",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Alle ablehnen",
    customize: "Einstellungen",
    privacy: "Datenschutzerklärung",
    imprint: "Impressum",
  },
  prefs: {
    // Distinct from the banner title on purpose: two dialogs with the same accessible
    // name are ambiguous to a screen reader moving between the layers.
    title: "Individuelle Datenschutz-Einstellungen",
    intro:
      "Wähle aus, welche Kategorien du erlaubst. Notwendige Dienste lassen sich nicht abwählen, " +
      "weil die Website ohne sie nicht funktioniert.",
    save: "Auswahl speichern",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Alle ablehnen",
    close: "Schließen",
    alwaysActive: "Immer aktiv",
    provider: "Anbieter",
    purpose: "Zweck",
    retention: "Speicherdauer",
    cookies: "Cookies",
    usTransfer: "Drittlandtransfer",
    usTransferNote:
      "Daten werden in die USA übertragen. Dort besteht kein dem EU-Recht gleichwertiges Datenschutzniveau.",
    privacyPolicy: "Datenschutzerklärung des Anbieters",
    noVendors: "Für diese Kategorie sind keine Dienste eingebunden.",
  },
  categories: {
    necessary: {
      title: "Notwendig",
      description:
        "Erforderlich für den Betrieb der Website: Sitzungsverwaltung, Sicherheit und das Speichern " +
        "dieser Datenschutz-Entscheidung. Diese Cookies setzen wir auf Grundlage von § 25 Abs. 2 TDDDG " +
        "ohne Einwilligung.",
    },
    functional: {
      title: "Funktional",
      description:
        "Merkt sich Einstellungen wie Sprache oder Region und ermöglicht eingebettete Inhalte " +
        "wie Karten und Videos.",
    },
    analytics: {
      title: "Statistik",
      description:
        "Misst, welche Seiten aufgerufen werden und woher Besucher kommen, um die Inhalte gezielt " +
        "zu verbessern. Die Auswertung erfolgt zusammengefasst, nicht personenbezogen ausgewertet.",
    },
    marketing: {
      title: "Marketing",
      description:
        "Erfasst dein Verhalten über Websites hinweg, um Werbung auszuspielen und deren Erfolg zu " +
        "messen. Dabei werden Daten an Werbenetzwerke übermittelt.",
    },
  },
  /**
   * Deutsche Fassung der US-Opt-out-Texte — gebraucht, wenn eine US-Seite deutschsprachige
   * Besucher hat. Es ist ein Hinweis, keine Frage: nach CCPA/CPRA ist die Verarbeitung
   * bereits zulässig, eine Bitte um Erlaubnis würde den Sachverhalt falsch darstellen.
   */
  us: {
    title: "Deine Datenschutz-Optionen",
    body:
      "Wir verwenden Cookies und ähnliche Technologien, um die Website zu betreiben, ihre " +
      "Nutzung zu messen und Werbung auszuspielen. Du kannst dem Verkauf und der Weitergabe " +
      "deiner personenbezogenen Daten jederzeit widersprechen.",
    optOut: "Meine Daten nicht verkaufen oder weitergeben",
    acknowledge: "OK",
    revisit: "Datenschutz-Optionen",
    prefsIntro:
      "Diese Kategorien sind aktiv, bis du sie abschaltest. Technisch notwendige Dienste " +
      "lassen sich nicht abwählen, weil die Website ohne sie nicht funktioniert.",
    gpcHonoured:
      "Dein Browser hat ein Global-Privacy-Control-Signal gesendet. Wir haben es umgesetzt " +
      "und dich bereits ausgetragen — du musst nichts weiter tun.",
    optedOut: "Du hast widersprochen.",
    necessary:
      "Erforderlich für den Betrieb der Website: Sitzungsverwaltung, Sicherheit und das " +
      "Speichern dieser Entscheidung. Diese Daten werden weder verkauft noch weitergegeben.",
  },
  revisit: { label: "Datenschutz-Einstellungen" },
  placeholder: {
    title: "Externer Inhalt",
    body:
      "Dieser Inhalt wird von {provider} geladen. Dabei werden Daten an den Anbieter übertragen, " +
      "unter anderem deine IP-Adresse.",
    load: "Inhalt einmalig laden",
    loadAlways: "{provider} immer erlauben",
  },
  a11y: {
    dialog: "Datenschutz-Hinweis",
    prefsDialog: "Detaillierte Datenschutz-Einstellungen",
  },
};
